import { copy } from '@ohear/shared'
import { Button, Card, Checkbox, Slider, useToast } from '@ohear/ui'
/**
 * Project-level QC threshold editor.
 *
 * Producer-facing form mounted under the per-project Setup route as a "QC
 * settings" section. Persists four scalars to the `qc_thresholds` jsonb on
 * the projects row via `useUpdateProjectQcThresholds`.
 *
 * Three sliders + one toggle:
 *   - clipping_threshold_db   :  -3.0 to -0.1   (default -0.3)
 *   - noise_floor_max_db      :  -80   to -40   (default -60)
 *   - length_variance_pct     :   5    to 30    (default 15)
 *   - misread_strict          :  boolean        (default false)
 *
 * Defaults live in `@ohear/shared` (`copy.qc.DEFAULT_QC_THRESHOLDS`) so the
 * server, the actor app, and the producer app can all agree on a single
 * source of truth.
 */
import { useState } from 'react'
import {
  type QcThresholdsInput,
  useUpdateProjectQcThresholds,
} from '../../lib/api/producer-projects'
import { useT } from '../../lib/i18n/use-t'

export interface QcThresholdsFormProps {
  projectId: string
  /** Initial values (from project detail). Falls back to defaults when null. */
  initial?: Partial<QcThresholdsInput> | null
}

export function QcThresholdsForm({ projectId, initial }: QcThresholdsFormProps) {
  const t = useT()
  const toast = useToast()
  const mutation = useUpdateProjectQcThresholds(projectId)

  const [values, setValues] = useState<QcThresholdsInput>(() => ({
    clipping_threshold_db:
      initial?.clipping_threshold_db ?? copy.qc.DEFAULT_QC_THRESHOLDS.clipping_threshold_db,
    noise_floor_max_db:
      initial?.noise_floor_max_db ?? copy.qc.DEFAULT_QC_THRESHOLDS.noise_floor_max_db,
    length_variance_pct:
      initial?.length_variance_pct ?? copy.qc.DEFAULT_QC_THRESHOLDS.length_variance_pct,
    misread_strict: initial?.misread_strict ?? copy.qc.DEFAULT_QC_THRESHOLDS.misread_strict,
  }))

  const restoreDefaults = () => {
    setValues({
      clipping_threshold_db: copy.qc.DEFAULT_QC_THRESHOLDS.clipping_threshold_db,
      noise_floor_max_db: copy.qc.DEFAULT_QC_THRESHOLDS.noise_floor_max_db,
      length_variance_pct: copy.qc.DEFAULT_QC_THRESHOLDS.length_variance_pct,
      misread_strict: copy.qc.DEFAULT_QC_THRESHOLDS.misread_strict,
    })
  }

  const onSave = async () => {
    try {
      await mutation.mutateAsync(values)
      toast.show({ severity: 'success', title: t(copy.qc.THRESHOLDS_SAVED) })
    } catch {
      toast.show({ severity: 'error', title: t(copy.qc.THRESHOLDS_SAVE_FAILED) })
    }
  }

  return (
    <Card>
      <header className="mb-4 flex flex-col gap-1">
        <h2 className="m-0 text-[16px] font-semibold text-[var(--text)]">
          {t(copy.qc.THRESHOLDS_TITLE)}
        </h2>
        <p className="m-0 text-[13px] text-[var(--text-2)]">{t(copy.qc.THRESHOLDS_INTRO)}</p>
      </header>

      <div className="space-y-5">
        <ThresholdRow
          label={t(copy.qc.THRESHOLDS_CLIPPING)}
          help={t(copy.qc.THRESHOLDS_CLIPPING_HELP)}
          value={values.clipping_threshold_db}
          unit="dBFS"
          format={(v) => v.toFixed(1)}
        >
          <Slider
            min={-3}
            max={-0.1}
            step={0.1}
            value={values.clipping_threshold_db}
            onChange={(e) =>
              setValues((v) => ({
                ...v,
                clipping_threshold_db: Number(e.currentTarget.value),
              }))
            }
            aria-label={t(copy.qc.THRESHOLDS_CLIPPING)}
          />
        </ThresholdRow>

        <ThresholdRow
          label={t(copy.qc.THRESHOLDS_NOISEFLOOR)}
          help={t(copy.qc.THRESHOLDS_NOISEFLOOR_HELP)}
          value={values.noise_floor_max_db}
          unit="dBFS"
          format={(v) => v.toFixed(0)}
        >
          <Slider
            min={-80}
            max={-40}
            step={1}
            value={values.noise_floor_max_db}
            onChange={(e) =>
              setValues((v) => ({
                ...v,
                noise_floor_max_db: Number(e.currentTarget.value),
              }))
            }
            aria-label={t(copy.qc.THRESHOLDS_NOISEFLOOR)}
          />
        </ThresholdRow>

        <ThresholdRow
          label={t(copy.qc.THRESHOLDS_LENGTH)}
          help={t(copy.qc.THRESHOLDS_LENGTH_HELP)}
          value={values.length_variance_pct}
          unit="%"
          format={(v) => v.toFixed(0)}
        >
          <Slider
            min={5}
            max={30}
            step={1}
            value={values.length_variance_pct}
            onChange={(e) =>
              setValues((v) => ({
                ...v,
                length_variance_pct: Number(e.currentTarget.value),
              }))
            }
            aria-label={t(copy.qc.THRESHOLDS_LENGTH)}
          />
        </ThresholdRow>

        <div className="pt-2">
          <Checkbox
            checked={values.misread_strict}
            onChange={(e) => setValues((v) => ({ ...v, misread_strict: e.currentTarget.checked }))}
            label={t(copy.qc.THRESHOLDS_MISREAD_STRICT)}
            description={t(copy.qc.THRESHOLDS_MISREAD_STRICT_HELP)}
          />
        </div>
      </div>

      <footer className="mt-5 flex items-center justify-end gap-2 border-t border-[var(--border)] pt-4">
        <Button variant="ghost" onClick={restoreDefaults} disabled={mutation.isPending}>
          {t(copy.qc.THRESHOLDS_RESTORE_DEFAULTS)}
        </Button>
        <Button variant="primary" onClick={onSave} loading={mutation.isPending}>
          {t(copy.qc.THRESHOLDS_SAVE)}
        </Button>
      </footer>
    </Card>
  )
}

interface ThresholdRowProps {
  label: string
  help: string
  value: number
  unit: string
  format: (value: number) => string
  children: React.ReactNode
}

function ThresholdRow({ label, help, value, unit, format, children }: ThresholdRowProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-medium text-[var(--text)]">{label}</span>
        <span className="font-mono text-[12px] tabular-nums text-[var(--text-2)]">
          {format(value)} {unit}
        </span>
      </div>
      {children}
      <p className="m-0 text-[12px] text-[var(--text-3)]">{help}</p>
    </div>
  )
}
